"use client";

import { Handle, type NodeProps, Position } from "@xyflow/react";
import { Check, ChevronDown, Shield } from "lucide-react";
import { memo, useState } from "react";
import { useDirection } from "../DirectionContext";
import type { ConsistencyNodeData, ConsistencyRuleInfo } from "../types";
import { AdminNodeActions } from "./shared/AdminNodeActions";
import { NodeFrame } from "./shared/NodeFrame";
import { StatusBadge } from "./shared/StatusBadge";
import { STATUS_COLORS } from "./shared/status-styles";

const DEFAULT_VISIBLE = 2;

function RuleRow({
  rule,
  isColorful,
}: {
  rule: ConsistencyRuleInfo;
  isColorful: boolean;
}) {
  return (
    <div className="flex items-center gap-1.5 text-[10px]">
      {isColorful ? (
        <Check size={10} className="text-emerald-600 shrink-0" />
      ) : (
        <span className="h-2.5 w-2.5 rounded-full bg-gray-300 shrink-0" />
      )}
      <span
        className={`${isColorful ? "text-gray-600" : "text-gray-400"} whitespace-nowrap`}
      >
        {rule.name}
      </span>
    </div>
  );
}

export const ConsistencyNode = memo(function ConsistencyNode({
  id,
  data,
}: NodeProps) {
  const d = data as ConsistencyNodeData;
  const dir = useDirection();
  const rules = d.rules ?? [];
  const colors = STATUS_COLORS[d.status];
  const isColorful =
    d.status === "active" || d.status === "completed" || d.status === "failed";
  const [expanded, setExpanded] = useState(false);
  const isActive = d.status === "active";

  let stateClass = "";
  if (d.status === "pending") {
    stateClass = "!bg-amber-50 !border-l-amber-200";
  } else if (isActive) {
    // Consistency rules have no streaming payload UI, so active is treated as waiting.
    stateClass =
      "pipeline-node-active-waiting !bg-amber-100 !border-l-amber-400";
  }

  const hasMore = rules.length > DEFAULT_VISIBLE;
  const visibleRules = expanded ? rules : rules.slice(0, DEFAULT_VISIBLE);
  const hiddenCount = rules.length - DEFAULT_VISIBLE;

  return (
    <NodeFrame
      status={d.status}
      className={`min-w-[200px] w-max max-w-[300px] ${stateClass}`}
    >
      <Handle
        type="target"
        position={dir === "LR" ? Position.Left : Position.Top}
        className="!bg-transparent !border-0 !w-0 !h-0"
      />
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <StatusBadge status={d.status} />
          <Shield size={14} className={`shrink-0 ${colors.icon}`} />
          <span
            className={`text-xs font-medium ${colors.text} whitespace-nowrap`}
          >
            {d.label}
          </span>
        </div>
        {d.ruleCount > 0 && (
          <span className="text-[10px] text-gray-400 tabular-nums shrink-0">
            {d.ruleCount}
          </span>
        )}
      </div>
      {visibleRules.length > 0 && (
        <div className="border-t border-gray-100 pt-1 mt-1 flex flex-col gap-0.5">
          {visibleRules.map((r) => (
            <RuleRow key={r.name} rule={r} isColorful={isColorful} />
          ))}
        </div>
      )}
      {hasMore && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="mt-0.5 flex items-center gap-0.5 text-[10px] text-gray-400 hover:text-gray-600 transition-colors"
        >
          <ChevronDown
            size={10}
            className={`transition-transform ${expanded ? "rotate-180" : ""}`}
          />
          {expanded ? "collapse" : `+${hiddenCount} more`}
        </button>
      )}
      {d.status === "completed" && d.ruleCount > 0 && rules.length === 0 && (
        <div className="mt-1 text-[10px] text-gray-500 flex items-center gap-2">
          <span className="text-emerald-600">Pass: {d.passedCount}</span>
          {d.failedCount > 0 && (
            <span className="text-amber-600">Warn: {d.failedCount}</span>
          )}
        </div>
      )}
      <AdminNodeActions
        nodeId={d.nodeId ?? id}
        nodeStatus={d.status}
        nodeExecutionDirectives={d.nodeExecutionDirectives}
        onAdminAction={d.onAdminAction}
      />
      <Handle
        type="source"
        position={dir === "LR" ? Position.Right : Position.Bottom}
        className="!bg-transparent !border-0 !w-0 !h-0"
      />
    </NodeFrame>
  );
});
